import type { Card as CardType, Suit } from '@/types';
import { motion } from 'framer-motion';

interface CardProps {
  card?: CardType;
  hidden?: boolean;
  size?: 'sm' | 'md' | 'lg';
  animate?: boolean;
  delay?: number;
}

const SUIT_SYMBOLS: Record<Suit, string> = {
  hearts: '♥',
  diamonds: '♦',
  clubs: '♣',
  spades: '♠',
};

const SUIT_COLORS: Record<Suit, string> = {
  hearts: 'text-red-500',
  diamonds: 'text-red-500',
  clubs: 'text-slate-800',
  spades: 'text-slate-800',
};

export default function Card({ card, hidden = false, size = 'md', animate = true, delay = 0 }: CardProps) {
  const sizeClasses = {
    sm: 'w-10 h-14 text-sm',
    md: 'w-16 h-22 text-lg',
    lg: 'w-20 h-28 text-xl',
  };

  if (hidden) {
    return (
      <motion.div
        initial={animate ? { rotateY: 180, scale: 0.8 } : false}
        animate={{ rotateY: 0, scale: 1 }}
        transition={{ delay, type: 'spring', stiffness: 200 }}
        className={`${sizeClasses[size]} bg-gradient-to-br from-blue-800 to-blue-900 rounded-lg border-2 border-white/20 shadow-lg flex items-center justify-center`}
      >
        <div className="w-3/4 h-3/4 border-2 border-white/10 rounded-md flex items-center justify-center">
          <span className="text-white/30 text-2xl">?</span>
        </div>
      </motion.div>
    );
  }

  if (!card) return null;

  const suitColor = SUIT_COLORS[card.suit];
  const suitSymbol = SUIT_SYMBOLS[card.suit];

  return (
    <motion.div
      initial={animate ? { rotateY: 180, scale: 0.8 } : false}
      animate={{ rotateY: 0, scale: 1 }}
      transition={{ delay, type: 'spring', stiffness: 200 }}
      className={`${sizeClasses[size]} bg-white rounded-lg border-2 border-slate-200 shadow-lg flex flex-col items-center justify-center relative overflow-hidden`}
    >
      {/* 左上角 */}
      <div className={`absolute top-1 left-1 ${suitColor} font-bold leading-none`}>
        <div className="text-xs">{card.rank}</div>
        <div className="text-xs">{suitSymbol}</div>
      </div>

      {/* 中心花色 */}
      <div className={`${suitColor} text-3xl font-bold`}>
        {suitSymbol}
      </div>

      {/* 右下角（倒置） */}
      <div className={`absolute bottom-1 right-1 ${suitColor} font-bold leading-none rotate-180`}>
        <div className="text-xs">{card.rank}</div>
        <div className="text-xs">{suitSymbol}</div>
      </div>

      {/* 装饰性背景图案 */}
      <div className={`absolute inset-0 opacity-5 ${suitColor} flex items-center justify-center text-6xl pointer-events-none`}>
        {suitSymbol}
      </div>
    </motion.div>
  );
}
